import { Box, Button, CircularProgress, FormControl, IconButton, InputLabel, Link, MenuItem, Select, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MRTTable } from '../../components/MRTTable';
import { Add, Edit, Visibility } from '@mui/icons-material';
import { EmployeesAPI } from '../../fetch/employee';
import { PrepaidDealCostsAPI } from '../../fetch/prepaid-deal-costs';
import { useToastContext } from '../../hooks';
import { useParams } from 'react-router-dom';
import { UserContext } from '../../context/user';
import { IRoleType } from '../../models/roles';
import { IPrepaidDealCost, AdvancesFinancialStatus } from '../../models/agent-draws-and-deductions/prepaid-deal-cost';
import { AddEditPrepaidDealCostModal } from './include/modals/AddEditPrepaidDealCostModal';

const buildConfig = require('../../buildSettings.json');

let cachedStatuses: { advancesFinancialStatus: AdvancesFinancialStatus[] } | null = null;

const AuditTrailCell: React.FC<{
  updatedDateString: string;
  createdDateString: string;
  updatedBy: string;
  className?: string;
}> = ({ updatedDateString = '', createdDateString = '', updatedBy = '', className = '' }) => {
  const Bold = (chunks: React.ReactNode) => (
    <Typography component="span" fontWeight="500" variant="caption">
      {chunks}
    </Typography>
  );

  if (updatedDateString) {
    return (
      <Typography variant="caption" color="text.secondary" className={className}>
        {updatedBy ? (
          <>
            Updated by {Bold(updatedBy)} on {Bold(updatedDateString)}
          </>
        ) : (
          <>Updated on {Bold(updatedDateString)}</>
        )}
      </Typography>
    );
  }

  if (createdDateString) {
    return (
      <Typography variant="caption" color="text.secondary" className={className}>
        {updatedBy ? (
          <>
            Added by {Bold(updatedBy)} on {Bold(createdDateString)}
          </>
        ) : (
          <>Added on {Bold(createdDateString)}</>
        )}
      </Typography>
    );
  }

  // Fallback
  return (
    <Typography variant="body2" color="text.disabled" fontStyle="italic" className={className}>
      —
    </Typography>
  );
};

interface TruncatedCellProps {
  text: string;
  className?: string;
}

const TruncatedCell: React.FC<TruncatedCellProps> = ({ text }) => {
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'unset',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Typography>
  );
};

interface FilesCellProps {
  files: { id: number; fileName: string }[];
  agentDeductionId: number;
  notify: (...args: any[]) => void;
}

const FilesCell: React.FC<FilesCellProps> = ({ files, agentDeductionId, notify }) => {
  const [loadingFileId, setLoadingFileId] = useState<number | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);

  const viewFile = async (file: { id: number; fileName: string }) => {
    setLoadingFileId(file.id);
    try {
      const response = await PrepaidDealCostsAPI.downloadFile(agentDeductionId, file.id);
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Create blob with proper type and open in new tab
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up URL after delay
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err: any) {
      notify('error', err?.detail || `Failed to open ${file.fileName}`);
    } finally {
      setLoadingFileId(null);
    }
  };

  const downloadFile = async (file: { id: number; fileName: string }) => {
    setDownloadingFileId(file.id);
    try {
      const response = await PrepaidDealCostsAPI.downloadFile(agentDeductionId, file.id);
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Get filename from Content header or fallback to file.fileName
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
      const filename = filenameMatch ? filenameMatch[1] : file.fileName;
      
      // Create blob and trigger download with correct filename
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      notify('error', err?.detail || `Failed to download ${file.fileName}`);
    } finally {
      setDownloadingFileId(null);
    }
  };

  if (!files || files.length === 0) {
    return <span>—</span>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {files.map((file) => (
        <Box
          key={file.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              viewFile(file);
            }}
            disabled={loadingFileId === file.id}
            sx={{ p: 0.25 }}
            title="View file"
          >
            {loadingFileId === file.id ? (
              <CircularProgress size={14} />
            ) : (
              <Visibility fontSize="small" color="primary" />
            )}
          </IconButton>
          {downloadingFileId === file.id ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Downloading...</Typography>
            </Box>
          ) : (
            <Link
              component="button"
              variant="caption"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                downloadFile(file);
              }}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
                textAlign: 'left',
                cursor: 'pointer',
              }}
              title={`Download: ${file.fileName}`}
            >
              {file.fileName}
            </Link>
          )}
        </Box>
      ))}
    </Box>
  );
};

const AgentDeductionsDetails = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { showToast } = useToastContext();
  const showToastRef = React.useRef(showToast);
  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);
  const notify = useCallback((...args: Parameters<typeof showToast>) => {
    showToastRef.current?.(...args);
  }, []);

  const [page, setPage] = useState(0);
  const { userContext } = React.useContext(UserContext);
  const [perPage, setRowsPerPage] = useState(25);
  const [recordCount, setRecordCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState({
    employeeId: '',
    firstName: '',
    lastName: '',
    loading: false,
  });
  const [rows, setRows] = useState<IPrepaidDealCost[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editRow, setEditRow] = useState<IPrepaidDealCost | null>(null);
  // Totals from API
  const [amountTotals, setAmountTotals] = useState(0);
  const [fsPortionTotals, setFsPortionTotals] = useState(0);
  const [creditTotals, setCreditTotals] = useState(0);
  const [balanceTotals, setBalanceTotals] = useState(0);
  const [statuses, setStatuses] = useState<{ advancesFinancialStatus: AdvancesFinancialStatus[] } | null>(cachedStatuses);
  const [refreshKey, setRefreshKey] = useState(0);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  // Advances Financial Status filter
  const [advancesFinancialStatusFilter, setAdvancesFinancialStatusFilter] = useState<number | undefined>(undefined);

  const fetchEmployee = useCallback(async () => {
    setEmployee((prev) => ({ ...prev, loading: true }));
    try {
      const response = await EmployeesAPI.fetchEmployee(employeeId);

      if (response) {
        setEmployee((prev) => ({
          ...prev,
          employeeId: employeeId || '',
          firstName: response.firstName,
          lastName: response.lastName,
        }));
      } else {
        setEmployee((prev) => ({ ...prev, firstName: '', lastName: '' }));
      }
    } catch (error: any) {
      notify('error', error?.detail || 'Error trying to retrieve Employee details. Please try again.');
    } finally {
      setEmployee((prev) => ({ ...prev, loading: false }));
    }
  }, [employeeId, notify]);

  // statuses
  useEffect(() => {
    if (cachedStatuses) {
      setStatuses(cachedStatuses);
      return;
    }

    const fetchStatuses = async () => {
      try {
        const res = await PrepaidDealCostsAPI.fetchStatuses();
        cachedStatuses = res;
        setStatuses(res);
      } catch (err) {
        // Silently fail - statuses not critical
      }
    };

    fetchStatuses();
  }, []);

  // Fetch prepaid deal costs - always sorted by ID ascending (sorting disabled for users)
  useEffect(() => {
    const fetchPrepaidDealCosts = async () => {
      setLoading(true);
      try {
        // Default sort by ID ascending
        const response = await PrepaidDealCostsAPI.getByAgentId(Number(employeeId), {
          page: page + 1,
          perPage,
          sort: 'Id:Asc',
          search: globalFilter,
          advancesFinancialStatusFilter,
        });
        setRows(response.records);
        setRecordCount(response.totalRecordCount);
        setAmountTotals(response.amountTotals);
        setFsPortionTotals(response.fsPortionTotals);
        setCreditTotals(response.creditTotals);
        setBalanceTotals(response.balanceTotals);
      } catch (error: any) {
        notify('error', error?.detail || 'Error trying to retrieve Prepaid Deal Costs. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchPrepaidDealCosts();
  }, [page, perPage, employeeId, notify, refreshKey, globalFilter, advancesFinancialStatusFilter]);

  useEffect(() => {
    if (!employeeId) {
      notify('error', 'Employee ID is missing');
      return;
    } else {
      fetchEmployee();
    }
  }, [fetchEmployee, employeeId, notify]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getStatusName = (id: number): string => {
    if (!statuses) return '';
    const status = statuses.advancesFinancialStatus?.find(s => s.value === id);
    return status?.description || `Status ${id}`;
  };

  const hasAddAccess =
    !!userContext?.roles?.has(IRoleType.Accounting) || !!userContext?.roles?.has(IRoleType.SystemAdmin);
  const handleOpenAdd = () => {
    if (hasAddAccess) {
      setEditRow(null);
      setIsModalOpen(true);
    }
  };
  const handleCloseModal = () => {
    if (!saving && !savingEdit) {
      setIsModalOpen(false);
      setEditRow(null);
    }
  };

  const handleCreate = async (payload: {
    date: string;
    amount: number;
    fsReimbursementPercent: number;
    memo?: string;
    notes?: string;
  }): Promise<number | void> => {
    if (saving) return;
    setSaving(true);
    try {
      const created = await PrepaidDealCostsAPI.create(Number(employeeId), payload);
      const createdRow: IPrepaidDealCost = {
        id: created.id,
        createdDateString: created.createdDateString,
        updatedDateString: created.updatedDateString,
        amount: created.amount,
        fsReimbursementPercent: created.fsReimbursementPercent,
        fsPortion: created.fsPortion,
        credit: created.credit ?? 0,
        balance: created.balance ?? 0,
        memo: created.memo,
        notes: created.notes,
        advancesFinancialStatusId: created.advancesFinancialStatusId,
        updatedBy: created.updatedBy,
        files: created.files,
        transactions: created.transactions ?? [],
      };

      setRows((prev) => [createdRow, ...prev]);
      setBalanceTotals((prev) => prev + createdRow.balance);
      setAmountTotals((prev) => prev + createdRow.amount);
      setFsPortionTotals((prev) => prev + createdRow.fsPortion);
      setRecordCount((prev) => prev + 1);
      // Return ID for file uploads, success toastt shown after complete flow in on FileUploaded
      return created.id;
    } catch (err: any) {
      notify('error', err?.detail || 'Failed to create prepaid deal cost');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (row: IPrepaidDealCost) => {
    if (!hasAddAccess) return;
    setEditRow(row);
    setIsModalOpen(true);
  };

  const handleEditSave = async (
    id: number,
    payload: {
      date: string;
      amount: number;
      fsReimbursementPercent: number;
      memo?: string;
      notes?: string;
      advancesFinancialStatusId?: number;
    }
  ) => {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      // Update prepaid deal cost details
      const updated = await PrepaidDealCostsAPI.update(id, {
        date: payload.date,
        amount: payload.amount,
        fsReimbursementPercent: payload.fsReimbursementPercent,
        memo: payload.memo,
        notes: payload.notes,
      });

      let updatedRow: IPrepaidDealCost = {
        id: updated.id,
        createdDateString: updated.createdDateString,
        updatedDateString: updated.updatedDateString,
        amount: updated.amount,
        fsReimbursementPercent: updated.fsReimbursementPercent,
        fsPortion: updated.fsPortion,
        credit: updated.credit ?? 0,
        balance: updated.balance ?? 0,
        memo: updated.memo,
        notes: updated.notes,
        advancesFinancialStatusId: updated.advancesFinancialStatusId,
        updatedBy: updated.updatedBy,
        files: updated.files,
        transactions: updated.transactions ?? [],
      };

      // Update status if changed
      const current = rows.find((r) => r.id === id);
      if (payload.advancesFinancialStatusId !== undefined && current?.advancesFinancialStatusId !== payload.advancesFinancialStatusId) {
        const statusUpdated = await PrepaidDealCostsAPI.updateFinancialStatus(id, payload.advancesFinancialStatusId);
        updatedRow = {
          id: statusUpdated.id,
          createdDateString: statusUpdated.createdDateString,
          updatedDateString: statusUpdated.updatedDateString,
          amount: statusUpdated.amount,
          fsReimbursementPercent: statusUpdated.fsReimbursementPercent,
          fsPortion: statusUpdated.fsPortion,
          credit: statusUpdated.credit ?? 0,
          balance: statusUpdated.balance ?? 0,
          memo: statusUpdated.memo,
          notes: statusUpdated.notes,
          advancesFinancialStatusId: statusUpdated.advancesFinancialStatusId,
          updatedBy: statusUpdated.updatedBy,
          files: statusUpdated.files,
          transactions: statusUpdated.transactions ?? [],
        };
      }

      // Update totals based on balance delta
      const prevBalance = Number(current?.balance ?? 0);
      const newBalance = Number(updatedRow.balance ?? prevBalance);
      const delta = newBalance - prevBalance;
      if (!Number.isNaN(delta) && delta !== 0) {
        setBalanceTotals((prev) => prev + delta);
      }

      setRows((prev) => prev.map((r) => (r.id === updatedRow.id ? { ...r, ...updatedRow } : r)));
      
    } catch (err: any) {
      notify('error', err?.detail || 'Failed to update prepaid deal cost');
    } finally {
      setSavingEdit(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    const num = Number(value ?? 0);
    if (isNaN(num)) return '$0.000';
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
  };

  // Base URL for Frankie proper to link to commission booking statements
  const frankieProperBaseUrl = `${buildConfig.FRANKLIN_ST_V2_URL}/v2/ActivityDocuments/CommissionBookingStatement`;

  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        enableSorting: false,
        enableGlobalFilter: true,
        footer: 'Totals',
      },
      {
        accessorKey: 'createdDateString',
        header: 'Date',
        size: 100,
        enableSorting: false, 
        enableGlobalFilter: false,
      },
      {
        accessorKey: 'amount',
        header: 'Deduction Amount', 
        size: 140,
        enableSorting: false, 
        enableGlobalFilter: false,
        muiTableHeadCellProps: { sx: { minWidth: 140, backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' } },
        muiTableBodyCellProps: { sx: { minWidth: 140 } },
        Cell: ({ cell }: any) => formatCurrency(Number(cell.getValue())),
        footer: formatCurrency(amountTotals),
      },
      {
        accessorKey: 'fsPortion',
        header: 'FS Portion',
        size: 120,
        enableSorting: false,
        enableGlobalFilter: false,
        Cell: ({ cell }: any) => formatCurrency(Number(cell.getValue())),
        footer: formatCurrency(fsPortionTotals),
      },
      {
        accessorKey: 'credit',
        header: 'Credit',
        size: 120,
        enableSorting: false,
        enableGlobalFilter: false,
        Cell: ({ cell }: any) => formatCurrency(Number(cell.getValue())),
        footer: formatCurrency(creditTotals),
      },
      {
        accessorKey: 'balance',
        header: 'Balance', 
        size: 130,
        enableSorting: false,
        enableGlobalFilter: false,
        Cell: ({ cell }: any) => formatCurrency(Number(cell.getValue())),
        footer: formatCurrency(balanceTotals),
      },
      {
        accessorKey: 'transactions',
        header: 'Transaction ID',
        size: 200,
        enableSorting: false,
        enableGlobalFilter: false,
        muiTableHeadCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none', backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' },
        },
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none' },
        },
        Cell: ({ row }: any) => {
          const transactions = row.original.transactions || [];
          if (transactions.length === 0) {
            return <span>—</span>;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {transactions.map((txn: { transactionId: string; commissionBookingId: number }, idx: number) => (
                <React.Fragment key={`${txn.transactionId}-${idx}`}>
                  {idx > 0 && ', '}
                  <Link
                    href={`${frankieProperBaseUrl}/${txn.commissionBookingId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{ cursor: 'pointer' }}
                  >
                    {txn.transactionId}
                  </Link>
                </React.Fragment>
              ))}
            </Box>
          );
        },
      },
      {
        accessorKey: 'memo',
        header: 'Memo',
        size: 150,
        enableSorting: false, 
        enableGlobalFilter: true, 
        muiTableHeadCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none', backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' },
        },
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none' },
        },
        Cell: ({ cell }: any) => <TruncatedCell text={String(cell.getValue() ?? '')} />,
      },
      {
        accessorKey: 'files',
        header: 'Files',
        size: 250,
        enableSorting: false,
        enableGlobalFilter: false,
        muiTableHeadCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', verticalAlign: 'middle', py: 1, backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6' },
        },
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', verticalAlign: 'top', py: 1 },
        },
        Cell: ({ row }: any) => (
          <FilesCell
            files={row.original.files}
            agentDeductionId={row.original.id}
            notify={notify}
          />
        ),
      },
      {
        accessorKey: 'advancesFinancialStatusId',
        header: 'Advance Financial Status',
        size: 180,
        enableSorting: false,
        enableGlobalFilter: false,
        muiTableHeadCellProps: { sx: { minWidth: 180, backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' } },
        muiTableBodyCellProps: { sx: { minWidth: 180 } },
        Cell: ({ row }: any) => {
          const statusId = row.original.advancesFinancialStatusId;
          const statusName = getStatusName(statusId);
          const isPaid = statusId === 2; // Paid = 2, Not Paid = 1
          return (
            <Typography
              variant="caption"
              sx={{
                color: isPaid ? 'success.main' : 'warning.main',
                fontWeight: 500,
              }}
            >
              {statusName || '—'}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        size: 150,
        enableSorting: false, 
        enableGlobalFilter: false,
        muiTableHeadCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none', backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' },
        },
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', maxWidth: 'none' },
        },
        Cell: ({ cell }: any) => <TruncatedCell text={String(cell.getValue() ?? '')} />,
      },
      {
        accessorKey: 'history',
        header: 'History',
        size: 250,
        enableSorting: false,
        enableGlobalFilter: false,
        muiTableHeadCellProps: {
          sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'none', backgroundColor: (theme: any) => (theme?.frankie?.searchBlue ?? '#0B3C5D'), color: '#eff4f6', verticalAlign: 'middle' },
        },
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'none' },
        },
        Cell: ({ row }: any) => (
          <AuditTrailCell
            updatedDateString={row.original.updatedDateString || ''}
            createdDateString={row.original.createdDateString}
            updatedBy={row.original.updatedBy || ''}
          />
        ),
      },
      {
        accessorKey: 'actions',
        header: hasAddAccess ? (
          <Button
            size="small"
            onClick={handleOpenAdd}
            variant="contained"
            sx={{
              minWidth: 90,
              height: 30,
              fontSize: '0.72rem',
              whiteSpace: 'nowrap',
              px: 0.5,
              '@media (max-width: 1290px)': { width: '100%' },
            }}
            startIcon={<Add />}
          >
            Add
          </Button>
        ) : (
          ''
        ),
        size: 80,
        enableSorting: false,
        enableColumnActions: false,
        enableHiding: false,
        enableGlobalFilter: false,
        Cell: ({ row }: any) =>
          hasAddAccess ? (
            <IconButton onClick={() => handleOpenEdit(row.original)}>
              <Edit />
            </IconButton>
          ) : (
            <span />
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAddAccess, amountTotals, fsPortionTotals, creditTotals, balanceTotals, getStatusName]);

  return (
    <div>
      <Box position="sticky" top={70} bgcolor="background.paper" zIndex={1} pb={2}>
        <Box display="flex" alignItems="center" gap={3} flexWrap="nowrap">
          <Typography variant={'h6'} sx={{ minWidth: 280, whiteSpace: 'nowrap' }}>
            Prepaid Deal Costs for {employee.firstName.length > 0 ? `${employee.firstName} ${employee.lastName}` : 'Unknown'}
          </Typography>

          {/* Advances Financial Status dropdown filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="advances-financial-status-filter-label">Financial Status</InputLabel>
            <Select
              labelId="advances-financial-status-filter-label"
              id="advances-financial-status-filter"
              value={advancesFinancialStatusFilter ?? ''}
              label="Financial Status"
              onChange={(e) => {
                const val = e.target.value;
                setAdvancesFinancialStatusFilter(val === '' ? undefined : Number(val));
                setPage(0);
              }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {statuses?.advancesFinancialStatus?.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box>
        <Box
          sx={{
            '& thead th:first-of-type': {
              minWidth: 80,
              width: 80,
              paddingLeft: '30px',
              textAlign: 'left',
            },
            '& tbody td:first-of-type': {
              minWidth: 80,
              width: 80,
              paddingLeft: '30px',
              textAlign: 'left',
            },
            '& tfoot td:first-of-type': {
              minWidth: 80,
              width: 80,
              paddingLeft: '30px',
              textAlign: 'left',
            },
            '& thead th:nth-of-type(2), & tbody td:nth-of-type(2), & tfoot td:nth-of-type(2)': {
              minWidth: '100px !important',
              width: '100px !important',
              maxWidth: '120px !important',
            },
            '& tbody td': {
              borderBottom: '1px solid rgb(212, 210, 210)',
            },
          }}
        >
          <MRTTable
            columns={columns as any}
            data={rows}
            loading={loading}
            manualPagination
            enableSorting={false} 
            totalRowCount={recordCount}
            pageIndex={page}
            pageSize={perPage}
            onPaginationChange={(updater: any) => {
              const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: perPage }) : updater;
              if (next?.pageIndex !== undefined) setPage(next.pageIndex);
              if (next?.pageSize !== undefined) setRowsPerPage(next.pageSize);
            }}
            globalFilter={globalFilter}
            onGlobalFilterChange={(val: any) => {
              setGlobalFilter(val ?? '');
              setPage(0); // Reset to first page when searching
            }}
          />
        </Box>
        <AddEditPrepaidDealCostModal
          open={isModalOpen}
          onClose={handleCloseModal}
          initialData={editRow}
          statuses={statuses}
          employeeName={`${employee.firstName} ${employee.lastName}`.trim()}
          onCreate={handleCreate}
          onEditSave={handleEditSave}
          saving={editRow ? savingEdit : saving}
          onFileUploaded={() => {
            setRefreshKey(prev => prev + 1);
            notify('success', editRow ? 'Prepaid Deal Cost updated' : 'Prepaid Deal Cost created');
            handleCloseModal();
          }}
        />
      </Box>
    </div>
  );
};

export default AgentDeductionsDetails;
